/**
 * Parse docs/USER_FAQ.md into Help & Support FAQ sections.
 * Markdown shape: ## section, ### question, body until next heading.
 */

function slugify(text) {
  const base = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return base.slice(0, 72) || 'item';
}

function uniqueId(base, used) {
  let id = base;
  let n = 2;
  while (used.has(id)) {
    id = `${base}-${n}`;
    n += 1;
  }
  used.add(id);
  return id;
}

function stripInlineMarkdown(text) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
}

function parseAnswerBlocks(bodyLines) {
  const blocks = [];
  let paragraph = [];
  let listType = null;
  let listItems = [];
  let inCodeFence = false;

  const flushParagraph = () => {
    if (paragraph.length === 0) {
      return;
    }
    blocks.push({ type: 'p', text: stripInlineMarkdown(paragraph.join(' ')) });
    paragraph = [];
  };

  const flushList = () => {
    if (!listType || listItems.length === 0) {
      listType = null;
      listItems = [];
      return;
    }
    blocks.push({ type: listType, items: listItems.map(stripInlineMarkdown) });
    listType = null;
    listItems = [];
  };

  for (const rawLine of bodyLines) {
    const line = rawLine.trimEnd();

    if (line.startsWith('```')) {
      inCodeFence = !inCodeFence;
      continue;
    }
    if (inCodeFence) {
      continue;
    }

    if (line.trim() === '' || line.trim() === '---') {
      flushParagraph();
      flushList();
      continue;
    }

    const ulMatch = line.match(/^-\s+(.*)$/);
    if (ulMatch) {
      flushParagraph();
      if (listType && listType !== 'ul') {
        flushList();
      }
      listType = 'ul';
      listItems.push(ulMatch[1]);
      continue;
    }

    const olMatch = line.match(/^\d+\.\s+(.*)$/);
    if (olMatch) {
      flushParagraph();
      if (listType && listType !== 'ol') {
        flushList();
      }
      listType = 'ol';
      listItems.push(olMatch[1]);
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();
  return blocks;
}

export function parseUserFaqMarkdown(markdown) {
  const lines = markdown.split('\n');
  const sections = [];
  const usedSectionIds = new Set();
  const usedItemIds = new Set();

  let currentSection = null;
  let currentQuestion = null;
  let currentBody = [];

  const flushItem = () => {
    if (!currentSection || !currentQuestion) {
      return;
    }
    const blocks = parseAnswerBlocks(currentBody);
    if (blocks.length === 0) {
      currentQuestion = null;
      currentBody = [];
      return;
    }
    currentSection.items.push({
      id: uniqueId(slugify(currentQuestion), usedItemIds),
      question: stripInlineMarkdown(currentQuestion),
      answerBlocks: blocks,
    });
    currentQuestion = null;
    currentBody = [];
  };

  const flushSection = () => {
    flushItem();
    if (currentSection && currentSection.items.length > 0) {
      sections.push(currentSection);
    }
    currentSection = null;
  };

  for (const line of lines) {
    if (line.startsWith('## ')) {
      flushSection();
      const title = line.slice(3).trim();
      currentSection = {
        id: uniqueId(slugify(title), usedSectionIds),
        title,
        items: [],
      };
      continue;
    }

    if (line.startsWith('### ')) {
      flushItem();
      currentQuestion = line.slice(4).trim();
      currentBody = [];
      continue;
    }

    if (currentQuestion) {
      currentBody.push(line);
    }
  }

  flushSection();
  return sections;
}

/** Match [label](url) segments for inline FAQ links. */
export function splitFaqInlineLinks(text) {
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match = re.exec(text);
  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'link', label: match[1], href: match[2] });
    lastIndex = match.index + match[0].length;
    match = re.exec(text);
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return parts.length > 0 ? parts : [{ type: 'text', value: text }];
}
